/**
 * P1.13 F2a — agregação canônica de métricas de execução por demand.
 *
 * Extraído de DemandDetailPanel.MetricsTab pra testabilidade pura.
 *
 * Fontes (em ordem de precedência pra cost/tokens LLM):
 *   1) handler_executions: fonte canônica (todo agent grava aqui via
 *      withHandlerExecution). É a totalidade do gasto LLM.
 *   2) agent_executions: subset usado pra contar agents distintos +
 *      cache_creation/cache_read tokens. NÃO somar junto com handler.
 *   3) tool_executions: tools com cost próprio (raro). Breakdown only.
 *   4) skill_executions: skills disparadas. Breakdown only.
 *
 * Total agregado = handler_executions (cobre tudo, evita dupla contagem).
 * Demais fontes aparecem como breakdown pra rastreabilidade.
 *
 * Fallback: se handler_executions vazio (legacy demands pré-B1) mas
 * agent_executions não, usa agent como total.
 */

export interface AggRow {
  tokens_input: number | null;
  tokens_output: number | null;
  cost_usd: number | null;
  duration_ms: number | null;
}

export interface AgentExecRowMin extends AggRow {
  id: string;
  agent_id: string | null;
  cache_creation_input_tokens: number | null;
  cache_read_input_tokens: number | null;
}

export interface AggSum {
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  durationMs: number;
}

export interface DemandExecAggregate {
  handlerCount: number;
  agentCount: number;
  toolCount: number;
  skillCount: number;
  agentsDistinct: number;
  cacheCreate: number;
  cacheRead: number;
  handlerSum: AggSum;
  agentSum: AggSum;
  toolSum: AggSum;
  skillSum: AggSum;
  total: AggSum;
  usedFallback: boolean;
}

export function sumExecRows<T extends AggRow>(rows: T[]): AggSum {
  return rows.reduce<AggSum>(
    (acc, r) => ({
      tokensIn: acc.tokensIn + (r.tokens_input ?? 0),
      tokensOut: acc.tokensOut + (r.tokens_output ?? 0),
      costUsd: acc.costUsd + Number(r.cost_usd ?? 0),
      durationMs: acc.durationMs + (r.duration_ms ?? 0),
    }),
    { tokensIn: 0, tokensOut: 0, costUsd: 0, durationMs: 0 }
  );
}

export function aggregateDemandExecutions(input: {
  handlerExecs: AggRow[];
  agentExecs: AgentExecRowMin[];
  toolExecs: AggRow[];
  skillExecs: AggRow[];
}): DemandExecAggregate {
  const handlerSum = sumExecRows(input.handlerExecs);
  const agentSum = sumExecRows(input.agentExecs);
  const toolSum = sumExecRows(input.toolExecs);
  const skillSum = sumExecRows(input.skillExecs);

  let cacheCreate = 0;
  let cacheRead = 0;
  const agents = new Set<string>();
  for (const ae of input.agentExecs) {
    cacheCreate += ae.cache_creation_input_tokens ?? 0;
    cacheRead += ae.cache_read_input_tokens ?? 0;
    if (ae.agent_id) agents.add(ae.agent_id);
  }

  const useFallbackAgent = handlerSum.tokensIn === 0 && agentSum.tokensIn > 0;
  const total = useFallbackAgent ? agentSum : handlerSum;

  return {
    handlerCount: input.handlerExecs.length,
    agentCount: input.agentExecs.length,
    toolCount: input.toolExecs.length,
    skillCount: input.skillExecs.length,
    agentsDistinct: agents.size,
    cacheCreate,
    cacheRead,
    handlerSum,
    agentSum,
    toolSum,
    skillSum,
    total,
    usedFallback: useFallbackAgent,
  };
}

/* ── Correção UX v2 W1.5 — execuções unificadas da demanda ────────────────── */
//
// Shape canônico de UMA execução, espelhando o item de `executions[]` do
// GET /v1/demands/:id/executions (api.getDemandExecutions). O fallback legacy
// (demand-executions-legacy.ts, pré-restart) normaliza as exec tables
// (handler/agent/tool/skill) pra ESTE mesmo shape — o painel tem um caminho
// único de consumo. `kind: 'tool'` e os campos cache_* só existem no caminho
// legacy (o endpoint novo não os expõe).

export type DemandExecutionKind = 'handler' | 'agent' | 'skill' | 'tool';

export interface DemandExecutionRow {
  id: string;
  kind: DemandExecutionKind;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  cost_usd: number | null;
  tokens_input: number | null;
  tokens_output: number | null;
  error: string | null;
  handler_name: string | null;
  skill_id: string | null;
  agent_id: string | null;
  task_id: string | null;
  trace_id: string | null;
  /** F3: nome/slug HUMANOS resolvidos server-side pelo endpoint novo
   *  (agente real por trás do @agent-runner, nome da skill). Ausentes no
   *  caminho legacy — consumidor degrada pro handler_name/ids. */
  actor_name?: string | null;
  actor_slug?: string | null;
  /** Alvo do runner (tool/agent/skill slug) quando a fonte o expõe.
   *  Endpoint novo ainda NÃO devolve (pendência backend) — a UI deriva do
   *  target_slug da task ligada; legacy popula com tool_slug. */
  target_slug?: string | null;
  cache_creation_input_tokens?: number | null;
  cache_read_input_tokens?: number | null;
}

/**
 * Adapter W1.5: agrega a lista UNIFICADA de execuções (endpoint novo ou
 * legacy normalizado) reaproveitando aggregateDemandExecutions — separa por
 * kind e preserva a precedência handler > agent (fallback) com tool/skill
 * como breakdown.
 */
export function aggregateUnifiedExecutions(rows: DemandExecutionRow[]): DemandExecAggregate {
  const handlerExecs: AggRow[] = [];
  const agentExecs: AgentExecRowMin[] = [];
  const toolExecs: AggRow[] = [];
  const skillExecs: AggRow[] = [];
  for (const r of rows) {
    if (r.kind === 'handler') handlerExecs.push(r);
    else if (r.kind === 'agent')
      agentExecs.push({
        id: r.id,
        agent_id: r.agent_id,
        tokens_input: r.tokens_input,
        tokens_output: r.tokens_output,
        cost_usd: r.cost_usd,
        duration_ms: r.duration_ms,
        cache_creation_input_tokens: r.cache_creation_input_tokens ?? null,
        cache_read_input_tokens: r.cache_read_input_tokens ?? null,
      });
    else if (r.kind === 'tool') toolExecs.push(r);
    else skillExecs.push(r);
  }
  return aggregateDemandExecutions({ handlerExecs, agentExecs, toolExecs, skillExecs });
}

/**
 * W1.5 — "Tentativas" do mxgrid do wireframe (dlgDemand → Métricas): maior
 * retry_count entre as tasks + 1 (a execução original conta como tentativa).
 * null quando não há tasks (caller exibe '—').
 */
export function attemptsFromTasks<T extends { retry_count?: number | null }>(
  tasks: T[]
): number | null {
  if (tasks.length === 0) return null;
  let maxRetry = 0;
  for (const t of tasks) {
    const r = typeof t.retry_count === 'number' && Number.isFinite(t.retry_count) ? t.retry_count : 0;
    if (r > maxRetry) maxRetry = r;
  }
  return maxRetry + 1;
}

/**
 * P1.13 F2b — counters reais derivados das tasks linkadas. Usado como
 * fallback quando demand counter (B12) não atualizou (legacy demands ou
 * trigger ainda não dispatcho).
 */
export function realCountsFromTasks<T extends { status: string }>(
  tasks: T[]
): { done: number; failed: number; cancelled: number; total: number } {
  const c = { done: 0, failed: 0, cancelled: 0, total: tasks.length };
  for (const t of tasks) {
    if (t.status === 'done') c.done++;
    else if (t.status === 'failed') c.failed++;
    else if (t.status === 'cancelled') c.cancelled++;
  }
  return c;
}
